import type { LocaleCode, OperationCategory } from '@/api/models';

/**
 * Operation categories, as every screen that shows one reads them.
 *
 * There is no label map here and there cannot be one: the set is **seeded per
 * tenant and then theirs** — renamed, added to, removed — so a category's name
 * arrives from the server as data, in both languages, and this file only decides
 * which of the two to show.
 */

/**
 * The category's name in the reader's language, falling back to the other.
 *
 * A tenant who filled only one language is a normal state once the set is
 * theirs to write, so the fallback is the ordinary path rather than a guard
 * against corruption — and an empty chip would be worse than one in the wrong
 * language.
 */
export const categoryLabel = (
    category: OperationCategory,
    locale: LocaleCode,
): string =>
    (locale === 'ka' ? category.nameKa : category.nameEn) ||
    category.nameEn ||
    category.nameKa;

/**
 * The categories a picker offers, in the server's display order.
 *
 * The order is carried rather than re-sorted: it is the tenant's own, and the
 * default set has a shape — the everyday kinds first, the residual last —
 * that neither language's alphabet preserves.
 */
export const categoryOptions = (
    categories: readonly OperationCategory[],
    locale: LocaleCode,
): { id: string; label: string }[] =>
    categories.map((category) => ({
        id: category.id,
        label: categoryLabel(category, locale),
    }));
